import React, { createContext, useState, useContext, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../config/firebase';
import toast from 'react-hot-toast';
import { useSlotCleanup } from '../hooks/useSlotCleanup';
import { useAppointmentExpiration } from '../hooks/useAppointmentExpiration';
import { useWaitlistCleanup } from '../hooks/useWaitlistCleanup';

// Services
import {
  getAllUsers,
  getUserById,
  updateUser,
  subscribeToUser,
  getOwnerByBarbershopId,
} from '../services/userService';
import { 
  subscribeToBarbershops,
  createBarbershop as createBarbershopService,
  updateBarbershop,
  getBarbershopById
} from '../services/barbershopService';
import {
  subscribeToUserAppointments,
  subscribeToBarbershopAppointments,
  createAppointment as createAppointmentService,
  updateAppointmentStatus as updateAppointmentService,
  getAppointmentById
} from '../services/appointmentService';
import { 
  createSlot as createSlotService,
  subscribeToSlots,
  markMultipleSlotsAsBooked,
  releaseMultipleSlots,
  // notifyWaitlistForSlot // DESABILITADO: causa CORS no frontend
} from '../services/slotService';
import {
  addToWaitlist as addToWaitlistService,
  cancelWaitlist as cancelWaitlistService,
  getWaitlistByUser
} from '../services/waitlistService';
import { createAuditLog } from '../services/auditService';
import {
  notifyAppointmentCreated,
  notifyAppointmentConfirmed,
  notifyAppointmentCancelled
} from '../services/notificationService';
// ✅ Backend Service para envio de emails (via Node.js API)
import {
  sendNewAppointmentEmail,
  sendConfirmationEmail,
  sendCancellationEmail
} from '../services/backendService';
// Google Calendar integration
import {
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
  checkCalendarConnection
} from '../services/googleCalendarService';

const AppContext = createContext();

export const AppProvider = ({ children }) => {
  // Auth state
  const [authUser, setAuthUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  
  // App state
  const [currentUser, setCurrentUser] = useState(null);
  const [barbershops, setBarbershops] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Limpeza automática de slots expirados (apenas para owners)
  useSlotCleanup(currentUser);
  
  // Auto-expiração de agendamentos pendentes (apenas para owners)
  useAppointmentExpiration(currentUser);
  
  // Limpeza automática de waitlist expirado (apenas para owners)
  useWaitlistCleanup(currentUser);

  // Firebase Auth Listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setAuthUser(firebaseUser);
        
        // Buscar dados do usuário no Firestore
        const result = await getUserById(firebaseUser.uid);
        if (result.success) {
          setCurrentUser(result.data);
        } else {
          // Se não encontrar no Firestore, criar perfil básico
          console.warn('User not found in Firestore, creating basic profile');
          setCurrentUser({
            id: firebaseUser.uid,
            name: firebaseUser.displayName || 'Usuário',
            email: firebaseUser.email,
            role: 'user'
          });
        }
      } else {
        setAuthUser(null);
        setCurrentUser(null);
      }
      setAuthLoading(false);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Listener real-time para dados do usuário atual
  useEffect(() => {
    if (!authUser?.uid) return;

    const unsubscribe = subscribeToUser(authUser.uid, (userData) => {
      if (userData) {
        setCurrentUser(userData);
      }
    });

    return () => unsubscribe();
  }, [authUser]);

  // Listener para Barbearias (real-time)
  useEffect(() => {
    const unsubscribe = subscribeToBarbershops((shops) => {
      setBarbershops(shops);
    });

    return () => unsubscribe();
  }, []);

  // Helper function: obter barbershopId do owner (suporta formato antigo e novo)
  const getOwnerBarbershopId = (user) => {
    if (!user || user.role !== 'owner') return null;
    // Formato antigo: string barbershopId
    if (user.barbershopId) return user.barbershopId;
    // Formato novo: array barbershops - pegar a primeira
    if (user.barbershops && user.barbershops.length > 0) return user.barbershops[0];
    return null;
  };

  // Listener para Agendamentos (real-time)
  useEffect(() => {
    if (!currentUser) return;

    let unsubscribe;

    if (currentUser.role === 'user') {
      // Cliente: ver seus próprios agendamentos
      unsubscribe = subscribeToUserAppointments(currentUser.id, setAppointments);
    } else if (currentUser.role === 'owner') {
      const barbershopId = getOwnerBarbershopId(currentUser);
      if (barbershopId) {
        // Dono: ver agendamentos da barbearia
        unsubscribe = subscribeToBarbershopAppointments(barbershopId, setAppointments);
      }
    } else if (currentUser.role === 'admin') {
      // Admin: pode precisar ver todos (implementar depois)
      // Por enquanto, vazio
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [currentUser]);

  // Listener real-time para slots disponíveis (owner e admin)
  useEffect(() => {
    if (!currentUser) return;
    
    console.log('🎯 Verificando listener de slots para role:', currentUser.role);
    
    let unsubscribe;
    
    if (currentUser.role === 'owner') {
      const barbershopId = getOwnerBarbershopId(currentUser);
      if (barbershopId) {
        console.log('👨‍💼 Iniciando listener para dono. BarbershopId:', barbershopId);
        unsubscribe = subscribeToSlots(barbershopId, (slots) => {
          console.log('📦 Slots atualizados no contexto:', slots.length);
          setAvailableSlots(slots);
        });
      }
    } else if (currentUser.role === 'admin') {
      // Admin não precisa de listener automático, vai carregar ao selecionar barbearia
      console.log('👑 Admin - sem listener automático');
      setAvailableSlots([]);
    }

    return () => {
      if (unsubscribe) {
        console.log('🛑 Desconectando listener de slots');
        unsubscribe();
      }
    };
  }, [currentUser]);

  // ===== FUNÇÕES DE AGENDAMENTO =====
  
  /**
   * Criar novo agendamento
   */
  const createAppointment = async (appointmentData) => {
    try {
      // ===== MULTI-SLOT BOOKING =====
      // Se slotIds já foi fornecido (do MultiSlotBookingModal), usar diretamente
      // Caso contrário, usar a lógica antiga com slotId único
      const slotIdsToUse = appointmentData.slotIds || (appointmentData.slotId ? [appointmentData.slotId] : []);
      
      if (slotIdsToUse.length === 0) {
        toast.error('Nenhum slot selecionado');
        return { success: false, error: 'No slots selected' };
      }
      // ===== FIM MULTI-SLOT BOOKING =====

      // Criar agendamento no Firestore
      const result = await createAppointmentService({
        ...appointmentData,
        userId: currentUser.id,
        status: 'pending',
        slotIds: slotIdsToUse
      });

      if (!result.success) {
        toast.error('Erro ao criar agendamento');
        return { success: false, error: result.error };
      }

      // ===== MARCAR MÚLTIPLOS SLOTS COMO RESERVADOS =====
      // Nota: markMultipleSlotsAsBooked já é chamado em BarbershopPage
      // mas mantemos aqui por segurança/compatibilidade
      if (slotIdsToUse.length > 0) {
        const bookingResult = await markMultipleSlotsAsBooked(
          slotIdsToUse,
          result.id
        );
        
        if (!bookingResult.success) {
          console.error('Erro ao marcar slots:', bookingResult.error);
          toast.error('Agendamento criado mas houve erro ao reservar todos os slots');
        }
      }
      // ===== FIM MARCAR SLOTS =====

      // Buscar dados da barbearia
      const barbershop = barbershops.find(b => b.id === appointmentData.barbershopId);
      
      if (!barbershop) {
        toast.error('Barbearia não encontrada');
        return { success: false, error: 'Barbershop not found' };
      }

      // Buscar o dono da barbearia (primeiro tenta pelo ownerId, depois busca no users)
      let ownerId = barbershop.ownerId;
      
      if (!ownerId) {
        // Se não tem ownerId na barbearia, buscar o dono pelo barbershopId
        const ownerResult = await getOwnerByBarbershopId(appointmentData.barbershopId);
        if (ownerResult.success) {
          ownerId = ownerResult.data.id;
        }
      }
      
      // Criar notificação interna para o dono
      if (ownerId) {
        await notifyAppointmentCreated({
          ...appointmentData,
          id: result.id
        }, ownerId);
        
        // ✅ EMAIL: Notificar dono sobre novo agendamento (via backend)
        const ownerResult = await getUserById(ownerId);
        if (ownerResult.success && ownerResult.data.email) {
          await sendNewAppointmentEmail(
            { ...appointmentData, id: result.id },
            barbershop,
            ownerResult.data.email
          );
          
          // ✅ GOOGLE CALENDAR: Criar evento automaticamente se conectado
          const calendarStatus = await checkCalendarConnection(ownerId);
          if (calendarStatus.connected && calendarStatus.tokenValid) {
            const calendarResult = await createCalendarEvent(
              ownerId,
              { ...appointmentData, id: result.id },
              barbershop
            );
            
            if (calendarResult.success) {
              // Salvar eventId no appointment para futuras atualizações
              await updateAppointmentService(result.id, 'pending', {
                googleCalendarEventId: calendarResult.eventId
              });
              console.log('✅ Evento criado no Google Calendar:', calendarResult.eventId);
            }
          }
        }
      } else {
        console.warn('No owner found for barbershop:', appointmentData.barbershopId);
      }

      // Enviar notificações externas (email/WhatsApp)
      // Comentado por enquanto até configurar Firebase Functions
      /*
      if (ownerId) {
        const ownerResult = await getUserById(ownerId);
        if (ownerResult.success) {
          await sendAppointmentCreatedNotification({
            ...appointmentData,
            id: result.id
          }, ownerResult.data);
        }
      }
      */

      // Registrar log de auditoria
      const barbershopResult = await getBarbershopById(appointmentData.barbershopId);
      const barbershopName = barbershopResult.success ? barbershopResult.data.name : 'Barbearia';

      await createAuditLog({
        action: 'create_appointment',
        userId: currentUser?.id,
        userName: currentUser?.name || currentUser?.email,
        userRole: currentUser?.role,
        targetType: 'appointment',
        targetId: result.id,
        targetName: `Agendamento de ${appointmentData.clientName}`,
        description: `Novo agendamento criado em ${barbershopName} para ${appointmentData.date} às ${appointmentData.time} - Serviço: ${appointmentData.service}`,
        barbershopId: appointmentData.barbershopId,
        details: {
          appointmentId: result.id,
          date: appointmentData.date,
          time: appointmentData.time,
          service: appointmentData.service,
          clientName: appointmentData.clientName
        }
      });

      toast.success('Solicitação de agendamento enviada! Aguarde a confirmação.');
      return { success: true, id: result.id };
    } catch (error) {
      console.error('Error in createAppointment:', error);
      toast.error('Erro ao criar agendamento');
      return { success: false, error: error.message };
    }
  };

  /**
   * Atualizar status do agendamento
   */
  const updateAppointmentStatus = async (appointmentId, status) => {
    try {
      const result = await updateAppointmentService(appointmentId, status);
      
      if (!result.success) {
        toast.error('Erro ao atualizar agendamento');
        return;
      }

      // Buscar dados do agendamento
      const appointmentResult = await getAppointmentById(appointmentId);
      if (!appointmentResult.success) return;
      
      const appointment = appointmentResult.data;

      // Criar notificações baseadas no status
      if (status === 'confirmed') {
        // Notificar cliente que foi confirmado
        await notifyAppointmentConfirmed(appointment, appointment.userId);
        
        // ✅ EMAIL: Enviar email de confirmação para o cliente (via backend)
        const clientResult = await getUserById(appointment.userId);
        if (clientResult.success && clientResult.data.email) {
          const barbershop = barbershops.find(b => b.id === appointment.barbershopId);
          if (barbershop) {
            await sendConfirmationEmail(appointment, barbershop, clientResult.data.email);
            
            // ✅ GOOGLE CALENDAR: Atualizar evento se existir
            if (appointment.googleCalendarEventId && barbershop.ownerId) {
              const calendarStatus = await checkCalendarConnection(barbershop.ownerId);
              if (calendarStatus.connected && calendarStatus.tokenValid) {
                await updateCalendarEvent(
                  barbershop.ownerId,
                  appointment.googleCalendarEventId,
                  appointment,
                  barbershop
                );
                console.log('✅ Evento atualizado no Google Calendar');
              }
            }
          }
        }
        
        toast.success('Agendamento confirmado!');
      } else if (status === 'cancelled') {
        // Determinar quem cancelou
        const cancelledBy = currentUser.role === 'owner' ? 'owner' : 'user';
        const notifyUserId = cancelledBy === 'owner' ? appointment.userId : appointment.barbershopId;
        
        await notifyAppointmentCancelled(appointment, notifyUserId, cancelledBy);
        
        // ✅ EMAIL: Enviar email de cancelamento (via backend)
        const barbershop = barbershops.find(b => b.id === appointment.barbershopId);
        if (barbershop) {
          // ✅ GOOGLE CALENDAR: Deletar evento se existir
          if (appointment.googleCalendarEventId && barbershop.ownerId) {
            const calendarStatus = await checkCalendarConnection(barbershop.ownerId);
            if (calendarStatus.connected && calendarStatus.tokenValid) {
              await deleteCalendarEvent(barbershop.ownerId, appointment.googleCalendarEventId);
              console.log('✅ Evento removido do Google Calendar');
            }
          }
          
          // Se dono cancelou, notifica o cliente
          if (cancelledBy === 'owner') {
            const clientResult = await getUserById(appointment.userId);
            if (clientResult.success && clientResult.data.email) {
              await sendCancellationEmail(appointment, barbershop, clientResult.data.email, cancelledBy);
            }
          }
          // Se cliente cancelou, notifica o dono
          else {
            const ownerResult = await getUserById(barbershop.ownerId);
            if (ownerResult.success && ownerResult.data.email) {
              await sendCancellationEmail(appointment, barbershop, ownerResult.data.email, cancelledBy);
            }
          }
        }
        
        // ✅ FILA DE ESPERA: Notificar usuários quando horário fica livre (via backend)
        // ✅ MULTI-SLOT: Liberar todos os slots quando cancelar
        if (appointment.slotIds && appointment.slotIds.length > 0) {
          const barbershop = barbershops.find(b => b.id === appointment.barbershopId);
          if (barbershop) {
            // Liberar múltiplos slots
            const releaseResult = await releaseMultipleSlots(appointment.slotIds);
            if (!releaseResult.success) {
              console.error('Erro ao liberar slots:', releaseResult.error);
            }
            
            // Criar objeto do slot com as informações do agendamento cancelado
            const slotData = {
              barbershopId: appointment.barbershopId,
              date: appointment.date,
              time: appointment.time
            };
            
            // Notificar usuários na fila de espera
            await notifyWaitlistUsers(slotData, barbershop);
          }
        }
        // Compatibilidade com versão antiga (slotId único)
        else if (appointment.slotId) {
          const barbershop = barbershops.find(b => b.id === appointment.barbershopId);
          if (barbershop) {
            // Liberar slot único
            await releaseMultipleSlots([appointment.slotId]);
            
            // Criar objeto do slot com as informações do agendamento cancelado
            const slotData = {
              barbershopId: appointment.barbershopId,
              date: appointment.date,
              time: appointment.time
            };
            
            // Notificar usuários na fila de espera
            await notifyWaitlistUsers(slotData, barbershop);
          }
        }
        
        toast.success('Agendamento cancelado');
      } else if (status === 'completed') {
        toast.success('Agendamento marcado como concluído');
      }

      // Registrar log de auditoria para mudança de status
      if (appointmentResult.success) {
        const appointment = appointmentResult.data;
        let action = 'update_appointment';
        let actionDescription = '';
        
        if (status === 'confirmed') {
          action = 'confirm_appointment';
          actionDescription = 'Agendamento confirmado';
        } else if (status === 'cancelled') {
          action = 'cancel_appointment';
          actionDescription = 'Agendamento cancelado';
        } else if (status === 'completed') {
          action = 'complete_appointment';
          actionDescription = 'Agendamento concluído';
        } else if (status === 'no-show') {
          action = 'no_show_appointment';
          actionDescription = 'Cliente não compareceu';
        } else {
          actionDescription = `Status alterado para ${status}`;
        }

        const barbershopResult = await getBarbershopById(appointment.barbershopId);
        const barbershopName = barbershopResult.success ? barbershopResult.data.name : 'Barbearia';

        await createAuditLog({
          action: action,
          userId: currentUser?.id,
          userName: currentUser?.name || currentUser?.email,
          userRole: currentUser?.role,
          targetType: 'appointment',
          targetId: appointmentId,
          targetName: `Agendamento de ${appointment.clientName}`,
          description: `${actionDescription} - ${barbershopName} em ${appointment.date} às ${appointment.time}`,
          barbershopId: appointment.barbershopId,
          changes: {
            status: {
              before: appointment.status,
              after: status
            }
          },
          details: {
            appointmentId: appointmentId,
            oldStatus: appointment.status,
            newStatus: status,
            date: appointment.date,
            time: appointment.time,
            clientName: appointment.clientName
          }
        });
      }

      return { success: true };
    } catch (error) {
      console.error('Error in updateAppointmentStatus:', error);
      toast.error('Erro ao atualizar status');
      return { success: false, error: error.message };
    }
  };

  // ===== FUNÇÕES DE BARBEARIA =====
  
  /**
   * Adicionar nova barbearia
   */
  const addBarbershop = async (barbershopData) => {
    try {
      const result = await createBarbershopService({
        ...barbershopData,
        ownerId: currentUser.id,
        createdBy: currentUser.id
      });

      if (!result.success) {
        toast.error('Erro ao criar barbearia');
        return;
      }

      // Registrar log de auditoria
      await createAuditLog({
        action: 'create_barbershop',
        userId: currentUser?.id,
        userName: currentUser?.name || currentUser?.email,
        userRole: currentUser?.role,
        targetType: 'barbershop',
        targetId: result.id,
        targetName: barbershopData.name,
        description: `Barbearia "${barbershopData.name}" criada em ${barbershopData.city}`,
        barbershopId: result.id,
        details: {
          barbershopName: barbershopData.name,
          ownerId: currentUser.id,
          city: barbershopData.city
        }
      });

      toast.success('Barbearia criada com sucesso!');
      return { success: true, id: result.id };
    } catch (error) {
      console.error('Error in addBarbershop:', error);
      toast.error('Erro ao criar barbearia');
      return { success: false, error: error.message };
    }
  };

  // ===== FUNÇÕES DE HORÁRIOS =====
  
  /**
   * Adicionar novo horário disponível
   */
  const addSlot = async (slotData) => {
    try {
      // Verificar se barbershopId foi passado nos dados ou pegar do currentUser
      const barbershopId = slotData.barbershopId || currentUser?.barbershopId;
      
      if (!barbershopId) {
        toast.error('ID da barbearia não encontrado');
        return { success: false, error: 'Missing barbershopId' };
      }

      const result = await createSlotService({
        ...slotData,
        barbershopId: barbershopId
      });

      if (!result.success) {
        toast.error(result.error || 'Erro ao adicionar horário');
        return { success: false, error: result.error };
      }

      // Registrar log de auditoria
      const barbershopResult = await getBarbershopById(barbershopId);
      const barbershopName = barbershopResult.success ? barbershopResult.data.name : 'Barbearia';

      await createAuditLog({
        action: 'create_slot',
        userId: currentUser?.id,
        userName: currentUser?.name || currentUser?.email,
        userRole: currentUser?.role,
        targetType: 'slot',
        targetId: result.id,
        targetName: `Horário ${slotData.time}`,
        description: `Novo horário criado em ${barbershopName} para ${slotData.date} às ${slotData.time}`,
        barbershopId: barbershopId,
        details: {
          slotId: result.id,
          date: slotData.date,
          time: slotData.time
        }
      });

      // Notificar usuários na fila de espera
      const barbershop = barbershops.find(b => b.id === barbershopId);
      if (barbershop) {
        const slotForNotification = {
          id: result.id,
          barbershopId: barbershopId,
          date: slotData.date,
          time: slotData.time
        };
        await notifyWaitlistUsers(slotForNotification, barbershop);
      }
      
      // O listener real-time vai atualizar automaticamente
      toast.success('Horário adicionado com sucesso!');
      return { success: true, id: result.id };
    } catch (error) {
      console.error('Error in addSlot:', error);
      toast.error('Erro ao adicionar horário');
      return { success: false, error: error.message };
    }
  };

  // Função para buscar todos os usuários (admin only)
  const fetchAllUsers = async () => {
    try {
      setLoading(true);
      const result = await getAllUsers();
      
      if (result.success) {
        setUsers(result.data);
        return { success: true };
      } else {
        toast.error('Erro ao buscar usuários');
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Error fetching all users:', error);
      toast.error('Erro ao buscar usuários');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Função para atualizar usuário (admin only)
  const updateUserData = async (userId, updates) => {
    try {
      setLoading(true);
      
      // Buscar dados antigos do usuário para comparação
      const oldUserResult = await getUserById(userId);
      const oldUser = oldUserResult.success ? oldUserResult.data : null;
      
      const result = await updateUser(userId, updates);
      
      if (result.success) {
        toast.success('Usuário atualizado com sucesso!');
        
        // Atualizar lista local de usuários
        setUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === userId ? { ...user, ...updates } : user
          )
        );
        
        // Se o usuário foi promovido a owner e tem barbershopId, atualizar a barbearia
        const newBarbershopId = updates.barbershopId || (updates.barbershops && updates.barbershops[0]);
        if (updates.role === 'owner' && newBarbershopId) {
          await updateBarbershop(newBarbershopId, {
            ownerId: userId
          });
        }
        
        // Registrar log de auditoria
        let action = 'update_user';
        let actionDescription = '';
        const changes = {};
        const details = {
          userId: userId,
          updatedFields: Object.keys(updates)
        };
        
        // Se mudou o role, usar ação específica
        if (oldUser && oldUser.role !== updates.role) {
          action = 'change_user_role';
          actionDescription = `Permissão alterada de ${oldUser.role} para ${updates.role}`;
          changes.role = {
            before: oldUser.role,
            after: updates.role
          };
          details.oldRole = oldUser.role;
          details.newRole = updates.role;
          details.barbershopId = updates.barbershopId;
        } else {
          actionDescription = 'Dados do usuário atualizados';
          // Registrar outras mudanças
          if (oldUser) {
            Object.keys(updates).forEach(key => {
              if (oldUser[key] !== updates[key]) {
                changes[key] = {
                  before: oldUser[key],
                  after: updates[key]
                };
              }
            });
          }
        }

        const userName = updates.name || oldUser?.name || 'Usuário';
        
        await createAuditLog({
          action: action,
          userId: currentUser?.id,
          userName: currentUser?.name || currentUser?.email,
          userRole: currentUser?.role,
          targetType: 'user',
          targetId: userId,
          targetName: userName,
          description: actionDescription || `Usuário ${userName} atualizado`,
          changes: changes,
          barbershopId: updates.barbershopId || null,
          details: details
        });
        
        return { success: true };
      } else {
        toast.error('Erro ao atualizar usuário');
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Erro ao atualizar usuário');
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // ==================== WAITLIST FUNCTIONS ====================
  
  /**
   * Adicionar usuário à fila de espera
   */
  const addToWaitlist = async (data) => {
    try {
      if (!currentUser) {
        toast.error('Você precisa estar logado');
        return { success: false, error: 'Not authenticated' };
      }

      const waitlistData = {
        ...data,
        userId: currentUser.id,
        userName: currentUser.name,
        userEmail: currentUser.email,
        userPhone: currentUser.phone
      };

      const result = await addToWaitlistService(waitlistData);
      
      if (result.success) {
        toast.success('Você foi adicionado à fila de espera! 🔔');
        
        // Atualizar lista local
        const userWaitlist = await getWaitlistByUser(currentUser.id);
        setWaitlist(userWaitlist);
        
        return { success: true };
      } else {
        toast.error('Erro ao entrar na fila de espera');
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Error adding to waitlist:', error);
      toast.error('Erro ao entrar na fila de espera');
      return { success: false, error: error.message };
    }
  };

  /**
   * Cancelar solicitação de fila de espera
   */
  const removeFromWaitlist = async (waitlistId) => {
    try {
      const result = await cancelWaitlistService(waitlistId);
      
      if (result.success) {
        toast.success('Removido da fila de espera');
        
        // Atualizar lista local
        setWaitlist(prev => prev.filter(w => w.id !== waitlistId));
        
        return { success: true };
      } else {
        toast.error('Erro ao sair da fila');
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Error removing from waitlist:', error);
      toast.error('Erro ao sair da fila');
      return { success: false, error: error.message };
    }
  };

  // Carregar waitlist do usuário quando logar
  useEffect(() => {
    if (currentUser) {
      getWaitlistByUser(currentUser.id).then(setWaitlist);
    } else {
      setWaitlist([]);
    }
  }, [currentUser]);

  /**
   * Notificar usuários da fila de espera quando horário fica livre
   */
  const notifyWaitlistUsers = async (slot, barbershop) => {
    try {
      // Usar a função completa do slotService que já tem notificação in-app
      const { notifyWaitlistForSlot } = await import('../services/slotService');
      const result = await notifyWaitlistForSlot(slot, barbershop);
      
      if (result.success && result.notified > 0) {
        console.log(`✅ ${result.notified} usuários notificados da fila de espera`);
      }
    } catch (error) {
      console.error('Erro ao notificar fila de espera:', error);
    }
  };

  const value = {
    // Auth
    authUser,
    authLoading,
    
    // User data
    currentUser,
    users,
    
    // App data
    barbershops,
    appointments,
    availableSlots,
    waitlist,
    loading,
    
    // Appointment functions
    createAppointment,
    updateAppointmentStatus,
    
    // Barbershop functions
    addBarbershop,
    
    // Slot functions
    addSlot,
    
    // Waitlist functions
    addToWaitlist,
    removeFromWaitlist,
    
    // User management functions (admin only)
    fetchAllUsers,
    updateUserData,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

// Hook customizado para consumir o contexto
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};
